import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { TranslationSections } from '@/types/language';

interface LanguagesEditorState {
  language: string | null;
  baseline: TranslationSections;
  target: TranslationSections;
  dirty: TranslationSections;
}

const initialState: LanguagesEditorState = { language: null, baseline: {}, target: {}, dirty: {} };

// Recompute the diff of target vs baseline (only changed fields, grouped by section).
export function computeDirty(target: TranslationSections, baseline: TranslationSections): TranslationSections {
  const out: TranslationSections = {};
  for (const section of Object.keys(target)) {
    for (const key of Object.keys(target[section] || {})) {
      if ((target[section][key] ?? '') !== (baseline[section]?.[key] ?? '')) {
        out[section] = out[section] || {};
        out[section][key] = target[section][key];
      }
    }
  }
  return out;
}

const slice = createSlice({
  name: 'languages',
  initialState,
  reducers: {
    loadTarget(state, action: PayloadAction<{ language: string; target: TranslationSections }>) {
      state.language = action.payload.language;
      state.target = action.payload.target;
      state.baseline = JSON.parse(JSON.stringify(action.payload.target));
      state.dirty = {};
    },
    editField(state, action: PayloadAction<{ section: string; key: string; value: string }>) {
      const { section, key, value } = action.payload;
      state.target[section] = { ...(state.target[section] || {}), [key]: value };
      state.dirty = computeDirty(state.target, state.baseline);
    },
    mergeAutoTranslated(state, action: PayloadAction<TranslationSections>) {
      for (const section of Object.keys(action.payload)) {
        state.target[section] = { ...(state.target[section] || {}), ...action.payload[section] };
      }
      state.dirty = computeDirty(state.target, state.baseline);
    },
    discard(state) {
      state.target = JSON.parse(JSON.stringify(state.baseline));
      state.dirty = {};
    },
    markSaved(state) {
      state.baseline = JSON.parse(JSON.stringify(state.target));
      state.dirty = {};
    },
  },
});

export const languagesActions = slice.actions;
export default slice.reducer;
